import React from 'react';
import Styled from 'styled';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';

import BoxView from 'four-nets/ui/box-view';
import UserAvatarView, { UserAvatarViewModel } from 'four-nets/user/user-avatar/user-avatar-view';
import { DeletedContent } from 'four-nets/common/deleted-content';
import { PurgedContent } from 'four-nets/common/purged-content';

import CommentBodyView, { CommentBodyViewModel } from './comment-body-view';
import defaultStyles from './comment-view.scss';

interface Comment<P> {
    userRole: string;
    user: User;
    gallery: ?(P[]);
    photoSwipeItem: (item: P) => { w: number, h: number, src: string, msrc: string };
    body: string;
    loadLikes: () => Like[];
    toggleLike: () => Promise<any>;
    isLikedByMe: boolean;
    mailSubject: (options: { intl: Intl }) => string;
    likesCount: number;
    complain: (options: { message: ?string }) => Promise<any>;
}

interface UserMentionable {
    mention(options: { username: string }): void;
}

export class CommentViewModel<P> {
    comment: Comment<P>;

    commentBodyViewModel: CommentBodyViewModel;
    userAvatarViewModel: UserAvatarViewModel;

    @observable collapse: boolean;

    constructor(options: {
        comment: Comment<P>,
        urlBuilder: URLBuilder,
        mailStore: MailStore,
        userMentionable: UserMentionable,
    }) {
        const { comment, urlBuilder, mailStore, userMentionable, userStore, categoryStore } = options;

        this.comment = comment;
        this.userStore = userStore;

        this.commentBodyViewModel = new CommentBodyViewModel({
            comment,
            urlBuilder,
            userMentionable,
            mailStore,
            userStore,
            categoryStore,
        });

        this.userAvatarViewModel = new UserAvatarViewModel({
            user: () => (comment.user.isAnonymous ? null : comment.user),
            role: () => comment.userRole,
        });

        const { displayedContent } = comment;

        this.isDeleted = false;
        if (displayedContent instanceof DeletedContent || displayedContent instanceof PurgedContent) {
            this.isDeleted = true;
        }
    }

    @action toggleCollapse = () => {
        this.collapse = !this.collapse;
    };
}

@Styled(defaultStyles)
@observer
class CommentView extends React.Component {
    render() {
        const { commentViewModel, styles, ...rest } = this.props;
        const { userAvatarViewModel, commentBodyViewModel, toggleCollapse } = commentViewModel;

        const { isFirstMessage } = rest;

        const classNames = [styles.comment];

        if (isFirstMessage) {
            classNames.push(styles.isFirstMessage);
        }

        const avatar = <UserAvatarView userAvatarViewModel={userAvatarViewModel} />;

        return (
            <div className={classNames.join(' ')}>
                {!commentViewModel.collapse ? (
                    <div className={styles.user}>
                        {avatar}
                        <CommentBodyView
                            commentBodyViewModel={commentBodyViewModel}
                            commentViewModel={commentViewModel}
                            {...rest}
                        />
                    </div>
                ) : (
                    <BoxView onClick={toggleCollapse}>
                        <FormattedMessage id='SHOW_FIRST_MESSAGE' />
                    </BoxView>
                )}
            </div>
        );
    }
}

export default CommentView;
